import React, { useState } from "react";

/** Escala 0-10 com slider e cor dinâmica */
export function ScaleField({ label, hint, value, onChange }) {
  const v = parseInt(value) || 0;
  const color = v <= 3 ? "emerald" : v <= 6 ? "amber" : "red";
  const colorMap = {
    emerald: { bg: "bg-emerald-100", text: "text-emerald-700", accent: "#10b981" },
    amber:   { bg: "bg-amber-100",   text: "text-amber-700",   accent: "#f59e0b" },
    red:     { bg: "bg-red-100",     text: "text-red-700",     accent: "#ef4444" },
  };
  const c = colorMap[color];
  return (
    <div className="space-y-2 col-span-1 md:col-span-2">
      <div className="flex items-center justify-between">
        <label className="text-xs font-semibold text-[hsl(var(--text-secondary))]">{label}</label>
        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${c.bg} ${c.text}`}>{v}</div>
      </div>
      <input type="range" min="0" max="10" value={v}
        style={{ accentColor: c.accent }}
        onChange={e => onChange(e.target.value)}
        className="w-full h-2 rounded-full cursor-pointer"
      />
      <div className="flex justify-between text-[10px] text-[hsl(var(--text-tertiary))]">
        <span>0 — Sem dor</span><span>5 — Moderada</span><span>10 — Insuportável</span>
      </div>
      {hint && <p className="text-[10px] text-[hsl(var(--text-tertiary))] italic">{hint}</p>}
    </div>
  );
}

/** Sim / Não / Não sei rápido */
export function RadioField({ label, hint, value, onChange, options = ["Sim", "Não", "Não sei"] }) {
  return (
    <div className="space-y-2">
      <label className="text-xs font-semibold text-[hsl(var(--text-secondary))]">{label}</label>
      <div className="flex flex-wrap gap-2">
        {options.map(opt => (
          <button key={opt} type="button"
            onClick={() => onChange(value === opt ? "" : opt)}
            className={`px-3 py-1.5 text-xs font-bold rounded-lg border transition-all ${
              value === opt
                ? "bg-[hsl(var(--brand))] text-white border-[hsl(var(--brand))]"
                : "bg-white text-[hsl(var(--text-secondary))] border-[hsl(var(--border-default))] hover:border-[hsl(var(--brand))]"
            }`}
          >{opt}</button>
        ))}
      </div>
      {hint && <p className="text-[10px] text-[hsl(var(--text-tertiary))] italic">{hint}</p>}
    </div>
  );
}

/** Chips multi-select com opções predefinidas */
export function ChipsField({ label, hint, value, onChange, options = [] }) {
  const selected = Array.isArray(value) ? value : [];
  const toggle = opt => selected.includes(opt) ? onChange(selected.filter(s => s !== opt)) : onChange([...selected, opt]);
  return (
    <div className="space-y-2 col-span-1 md:col-span-2">
      <label className="text-xs font-semibold text-[hsl(var(--text-secondary))]">{label}</label>
      <div className="flex flex-wrap gap-2">
        {options.map(opt => (
          <button key={opt} type="button" onClick={() => toggle(opt)}
            className={`px-2.5 py-1 text-xs font-medium rounded-full border transition-all ${
              selected.includes(opt)
                ? "bg-[hsl(var(--brand))] text-white border-[hsl(var(--brand))]"
                : "bg-white text-[hsl(var(--text-secondary))] border-[hsl(var(--border-default))] hover:border-[hsl(var(--brand))]/40"
            }`}
          >{opt}</button>
        ))}
      </div>
      {hint && <p className="text-[10px] text-[hsl(var(--text-tertiary))] italic">{hint}</p>}
    </div>
  );
}

/** Sim/Não com campo de detalhe condicional (tabagismo, álcool etc.) */
export function YesNoDetailField({ label, hint, value = {}, onChange, detailLabel = "Descreva" }) {
  const v = typeof value === "object" && value !== null ? value : {};
  const opts = ["Sim", "Não", "Ex-usuário"];
  return (
    <div className="space-y-2">
      <label className="text-xs font-semibold text-[hsl(var(--text-secondary))]">{label}</label>
      <div className="flex gap-2 flex-wrap">
        {opts.map(opt => (
          <button key={opt} type="button"
            onClick={() => onChange({ ...v, status: v.status === opt ? "" : opt })}
            className={`px-3 py-1.5 text-xs font-bold rounded-lg border transition-all ${
              v.status === opt
                ? "bg-[hsl(var(--brand))] text-white border-[hsl(var(--brand))]"
                : "bg-white text-[hsl(var(--text-secondary))] border-[hsl(var(--border-default))] hover:border-[hsl(var(--brand))]"
            }`}
          >{opt}</button>
        ))}
      </div>
      {(v.status === "Sim" || v.status === "Ex-usuário") && (
        <input className="ds-input text-sm mt-1" placeholder={detailLabel}
          value={v.detalhe || ""} onChange={e => onChange({ ...v, detalhe: e.target.value })} />
      )}
      {hint && <p className="text-[10px] text-[hsl(var(--text-tertiary))] italic">{hint}</p>}
    </div>
  );
}

const UP_RIGHT = [18, 17, 16, 15, 14, 13, 12, 11];
const UP_LEFT  = [21, 22, 23, 24, 25, 26, 27, 28];
const LOW_RIGHT = [48, 47, 46, 45, 44, 43, 42, 41];
const LOW_LEFT  = [31, 32, 33, 34, 35, 36, 37, 38];

const FACE_LABELS = {
  vestibular: "Vestibular (V)",
  oclusal: "Oclusal / Incisal (O)",
  mesial: "Mesial (M)",
  distal: "Distal (D)",
  palatina: "Palatina / Lingual (L)"
};

const FACE_COLORS = {
  nenhum: { fill: "#ffffff", stroke: "#d1d5db", label: "Saudável", badge: "bg-gray-100 text-gray-700" },
  carie: { fill: "rgba(239, 68, 68, 0.25)", stroke: "rgba(239, 68, 68, 1)", label: "Patologia / Cárie", badge: "bg-red-100 text-red-700 border-red-200" },
  planejado: { fill: "rgba(59, 130, 246, 0.25)", stroke: "rgba(59, 130, 246, 1)", label: "Tratamento Planejado", badge: "bg-blue-100 text-blue-700 border-blue-200" },
  realizado: { fill: "rgba(16, 185, 129, 0.25)", stroke: "rgba(16, 185, 129, 1)", label: "Tratamento Realizado", badge: "bg-emerald-100 text-emerald-700 border-emerald-200" }
};

export function OdontogramaField({ label, hint, value = {}, onChange }) {
  const [selectedTooth, setSelectedTooth] = useState(null);

  const parsedValue = typeof value === "object" && value !== null ? value : {};

  const handleToothUpdate = (toothNum, updater) => {
    const toothData = parsedValue[toothNum] || { status: "normal", faces: {}, notes: "" };
    const updatedTooth = updater(toothData);
    onChange({
      ...parsedValue,
      [toothNum]: updatedTooth
    });
  };

  const getToothData = (toothNum) => {
    return parsedValue[toothNum] || { status: "normal", faces: {}, notes: "" };
  };

  const activeToothData = selectedTooth ? getToothData(selectedTooth) : null;

  return (
    <div className="space-y-6 col-span-1 md:col-span-2">
      <div className="flex flex-col gap-1 border-b border-[hsl(var(--border-divider))] pb-3">
        <label className="text-sm font-bold text-[hsl(var(--text-primary))] uppercase tracking-wider">{label}</label>
        {hint && <p className="text-xs text-[hsl(var(--text-tertiary))] italic">{hint}</p>}
      </div>

      {/* Legenda */}
      <div className="flex flex-wrap gap-4 p-3 bg-[hsl(var(--bg-base))]/40 rounded-xl border border-[hsl(var(--border-default))] text-xs font-medium">
        <div className="flex items-center gap-2">
          <span className="w-3.5 h-3.5 rounded bg-white border border-gray-400" />
          <span>Saudável</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-3.5 h-3.5 rounded bg-red-100 border border-red-500" />
          <span>Patologia / Cárie</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-3.5 h-3.5 rounded bg-blue-100 border border-blue-500" />
          <span>Tratamento Planejado</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-3.5 h-3.5 rounded bg-emerald-100 border border-emerald-500" />
          <span>Tratamento Realizado</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="relative w-3.5 h-3.5 flex items-center justify-center font-bold text-gray-500">❌</span>
          <span>Ausente / Extraído</span>
        </div>
      </div>

      {/* Mapa Dentário */}
      <div className="p-4 md:p-6 bg-white border border-[hsl(var(--border-default))] rounded-2xl shadow-sm space-y-6 overflow-x-auto no-scrollbar">
        {/* Arcada Superior */}
        <div className="space-y-2 min-w-[700px]">
          <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest text-center">Arcada Superior (Quadrantes 1 e 2)</div>
          <div className="flex items-center justify-center gap-6">
            {/* Quadrante 1 - Superior Direito */}
            <div className="flex gap-1">
              {UP_RIGHT.map(num => (
                <Tooth 
                  key={num} 
                  number={num} 
                  data={parsedValue} 
                  isActive={selectedTooth === num} 
                  onClick={() => setSelectedTooth(num)} 
                />
              ))}
            </div>

            {/* Linha Divisória Vertical Central */}
            <div className="w-[2px] h-16 bg-gray-200" />

            {/* Quadrante 2 - Superior Esquerdo */}
            <div className="flex gap-1">
              {UP_LEFT.map(num => (
                <Tooth 
                  key={num} 
                  number={num} 
                  data={parsedValue} 
                  isActive={selectedTooth === num} 
                  onClick={() => setSelectedTooth(num)} 
                />
              ))}
            </div>
          </div>
        </div>

        {/* Divisória Horizontal da Boca */}
        <div className="flex items-center justify-center gap-2 min-w-[700px]">
          <div className="flex-1 h-[1px] bg-dashed bg-gray-200" />
          <span className="text-[10px] font-bold text-gray-300 uppercase tracking-wider px-2">Linha de Oclusão</span>
          <div className="flex-1 h-[1px] bg-dashed bg-gray-200" />
        </div>

        {/* Arcada Inferior */}
        <div className="space-y-2 min-w-[700px]">
          <div className="flex items-center justify-center gap-6">
            {/* Quadrante 4 - Inferior Direito */}
            <div className="flex gap-1">
              {LOW_RIGHT.map(num => (
                <Tooth 
                  key={num} 
                  number={num} 
                  data={parsedValue} 
                  isActive={selectedTooth === num} 
                  onClick={() => setSelectedTooth(num)} 
                />
              ))}
            </div>

            {/* Linha Divisória Vertical Central */}
            <div className="w-[2px] h-16 bg-gray-200" />

            {/* Quadrante 3 - Inferior Esquerdo */}
            <div className="flex gap-1">
              {LOW_LEFT.map(num => (
                <Tooth 
                  key={num} 
                  number={num} 
                  data={parsedValue} 
                  isActive={selectedTooth === num} 
                  onClick={() => setSelectedTooth(num)} 
                />
              ))}
            </div>
          </div>
          <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest text-center">Arcada Inferior (Quadrantes 4 e 3)</div>
        </div>
      </div>

      {/* Detalhes do Dente Selecionado */}
      {selectedTooth ? (
        <div className="ds-card p-5 bg-[hsl(var(--bg-base))]/30 border-2 border-[hsl(var(--brand))]/20 ds-fade-up space-y-5">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between border-b border-[hsl(var(--border-divider))] pb-3 gap-2">
            <div>
              <h4 className="text-base font-bold text-[hsl(var(--text-primary))]" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Configuração do Dente {selectedTooth}
              </h4>
              <p className="text-xs text-[hsl(var(--text-tertiary))]">Configure o status do dente ou clique em suas faces para adicionar patologias ou tratamentos.</p>
            </div>
            <button 
              type="button" 
              onClick={() => setSelectedTooth(null)} 
              className="text-xs text-[hsl(var(--text-tertiary))] hover:text-[hsl(var(--text-primary))] font-bold uppercase tracking-wider bg-white border border-[hsl(var(--border-default))] px-3 py-1.5 rounded-lg shadow-sm hover:bg-gray-50 transition-colors self-start sm:self-auto"
            >
              Fechar Detalhes
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Esquerda: Visualização Interativa Ampliada */}
            <div className="flex flex-col items-center justify-center p-4 bg-white border border-[hsl(var(--border-default))] rounded-xl shadow-inner">
              <span className="text-[10px] font-bold text-gray-400 mb-4 uppercase tracking-widest">Clique nas Faces Clínicas</span>
              <div className="relative w-28 h-28">
                <svg viewBox="0 0 40 40" className="w-full h-full cursor-pointer select-none">
                  {/* Vestibular (Top) */}
                  <path 
                    d="M 0,0 L 40,0 L 28,12 L 12,12 Z" 
                    fill={FACE_COLORS[activeToothData.faces.vestibular || "nenhum"].fill} 
                    stroke={activeToothData.faces.vestibular ? FACE_COLORS[activeToothData.faces.vestibular].stroke : "#d1d5db"} 
                    strokeWidth="1.5"
                    onClick={() => {
                      if (activeToothData.status === "ausente") return;
                      const current = activeToothData.faces.vestibular || "nenhum";
                      const next = current === "nenhum" ? "carie" : current === "carie" ? "planejado" : current === "planejado" ? "realizado" : "nenhum";
                      handleToothUpdate(selectedTooth, t => ({ ...t, faces: { ...t.faces, vestibular: next } }));
                    }}
                    className="hover:opacity-90 transition-opacity"
                  />
                  {/* Distal (Right) */}
                  <path 
                    d="M 40,0 L 28,12 L 28,28 L 40,40 Z" 
                    fill={FACE_COLORS[activeToothData.faces.distal || "nenhum"].fill} 
                    stroke={activeToothData.faces.distal ? FACE_COLORS[activeToothData.faces.distal].stroke : "#d1d5db"} 
                    strokeWidth="1.5"
                    onClick={() => {
                      if (activeToothData.status === "ausente") return;
                      const current = activeToothData.faces.distal || "nenhum";
                      const next = current === "nenhum" ? "carie" : current === "carie" ? "planejado" : current === "planejado" ? "realizado" : "nenhum";
                      handleToothUpdate(selectedTooth, t => ({ ...t, faces: { ...t.faces, distal: next } }));
                    }}
                    className="hover:opacity-90 transition-opacity"
                  />
                  {/* Palatina/Lingual (Bottom) */}
                  <path 
                    d="M 12,28 L 28,28 L 40,40 L 0,40 Z" 
                    fill={FACE_COLORS[activeToothData.faces.palatina || "nenhum"].fill} 
                    stroke={activeToothData.faces.palatina ? FACE_COLORS[activeToothData.faces.palatina].stroke : "#d1d5db"} 
                    strokeWidth="1.5"
                    onClick={() => {
                      if (activeToothData.status === "ausente") return;
                      const current = activeToothData.faces.palatina || "nenhum";
                      const next = current === "nenhum" ? "carie" : current === "carie" ? "planejado" : current === "planejado" ? "realizado" : "nenhum";
                      handleToothUpdate(selectedTooth, t => ({ ...t, faces: { ...t.faces, palatina: next } }));
                    }}
                    className="hover:opacity-90 transition-opacity"
                  />
                  {/* Mesial (Left) */}
                  <path 
                    d="M 0,0 L 12,12 L 12,28 L 0,40 Z" 
                    fill={FACE_COLORS[activeToothData.faces.mesial || "nenhum"].fill} 
                    stroke={activeToothData.faces.mesial ? FACE_COLORS[activeToothData.faces.mesial].stroke : "#d1d5db"} 
                    strokeWidth="1.5"
                    onClick={() => {
                      if (activeToothData.status === "ausente") return;
                      const current = activeToothData.faces.mesial || "nenhum";
                      const next = current === "nenhum" ? "carie" : current === "carie" ? "planejado" : current === "planejado" ? "realizado" : "nenhum";
                      handleToothUpdate(selectedTooth, t => ({ ...t, faces: { ...t.faces, mesial: next } }));
                    }}
                    className="hover:opacity-90 transition-opacity"
                  />
                  {/* Oclusal/Incisal (Center) */}
                  <path 
                    d="M 12,12 L 28,12 L 28,28 L 12,28 Z" 
                    fill={FACE_COLORS[activeToothData.faces.oclusal || "nenhum"].fill} 
                    stroke={activeToothData.faces.oclusal ? FACE_COLORS[activeToothData.faces.oclusal].stroke : "#d1d5db"} 
                    strokeWidth="1.5"
                    onClick={() => {
                      if (activeToothData.status === "ausente") return;
                      const current = activeToothData.faces.oclusal || "nenhum";
                      const next = current === "nenhum" ? "carie" : current === "carie" ? "planejado" : current === "planejado" ? "realizado" : "nenhum";
                      handleToothUpdate(selectedTooth, t => ({ ...t, faces: { ...t.faces, oclusal: next } }));
                    }}
                    className="hover:opacity-90 transition-opacity"
                  />
                  {activeToothData.status === "ausente" && (
                    <>
                      <line x1="2" y1="2" x2="38" y2="38" stroke="rgba(107, 114, 128, 0.7)" strokeWidth="3" strokeLinecap="round" />
                      <line x1="38" y1="2" x2="2" y2="38" stroke="rgba(107, 114, 128, 0.7)" strokeWidth="3" strokeLinecap="round" />
                    </>
                  )}
                </svg>
              </div>
            </div>

            {/* Centro: Controle de Status Global e das Faces */}
            <div className="space-y-4">
              <div>
                <span className="text-xs font-bold text-[hsl(var(--text-secondary))] block mb-2 uppercase tracking-widest">Status Geral do Dente</span>
                <label className="flex items-center gap-2 p-3 bg-white border border-[hsl(var(--border-default))] rounded-xl cursor-pointer hover:bg-gray-50 transition-colors shadow-sm">
                  <input 
                    type="checkbox" 
                    className="w-4 h-4 rounded border-gray-300 text-[hsl(var(--brand))] focus:ring-[hsl(var(--brand))]"
                    checked={activeToothData.status === "ausente"}
                    onChange={(e) => {
                      const isChecked = e.target.checked;
                      handleToothUpdate(selectedTooth, t => ({
                        ...t,
                        status: isChecked ? "ausente" : "normal",
                        faces: isChecked ? {} : t.faces
                      }));
                    }}
                  />
                  <div className="text-xs font-bold text-gray-700">DENTE AUSENTE / EXTRAÍDO</div>
                </label>
              </div>

              {activeToothData.status !== "ausente" && (
                <div className="space-y-3">
                  <span className="text-xs font-bold text-[hsl(var(--text-secondary))] block uppercase tracking-widest">Configurar Faces Clínicas</span>
                  <div className="space-y-2 max-h-[160px] overflow-y-auto pr-1">
                    {Object.keys(FACE_LABELS).map(faceKey => {
                      const curStatus = activeToothData.faces[faceKey] || "nenhum";
                      return (
                        <div key={faceKey} className="flex items-center justify-between gap-3 text-xs p-2 bg-white border border-[hsl(var(--border-default))] rounded-lg shadow-sm">
                          <span className="font-bold text-gray-700">{FACE_LABELS[faceKey]}</span>
                          <select
                            className="bg-gray-50 border border-gray-200 text-gray-700 py-1 px-2 rounded font-medium text-xs focus:ring-[hsl(var(--brand))] focus:border-[hsl(var(--brand))]"
                            value={curStatus}
                            onChange={(e) => {
                              handleToothUpdate(selectedTooth, t => ({
                                ...t,
                                faces: { ...t.faces, [faceKey]: e.target.value }
                              }));
                            }}
                          >
                            <option value="nenhum">Saudável / Nenhum</option>
                            <option value="carie">Patologia / Cárie</option>
                            <option value="planejado">Tratamento Planejado</option>
                            <option value="realizado">Tratamento Realizado</option>
                          </select>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Direita: Notas Clínicas */}
            <div className="space-y-2">
              <span className="text-xs font-bold text-[hsl(var(--text-secondary))] block uppercase tracking-widest">Anotações do Dente {selectedTooth}</span>
              <textarea
                className="ds-textarea h-[160px] text-xs font-medium bg-white focus:bg-white resize-none border border-[hsl(var(--border-default))] rounded-xl p-3 focus:ring-[hsl(var(--brand))]"
                placeholder="Ex: Fratura na cúspide, necessidade de coroa metálica ou infiltração observada na resina antiga..."
                value={activeToothData.notes || ""}
                onChange={(e) => {
                  handleToothUpdate(selectedTooth, t => ({ ...t, notes: e.target.value }));
                }}
              />
            </div>
          </div>
        </div>
      ) : (
        <div className="p-5 text-center border-2 border-dashed border-gray-200 rounded-xl bg-gray-50/50 text-xs text-gray-400 font-bold uppercase tracking-wider">
          💡 Clique em qualquer dente no mapa acima para configurar patologias, tratamentos ou adicionar observações.
        </div>
      )}
    </div>
  );
}

function Tooth({ number, data, isActive, onClick }) {
  const toothData = data[number] || { status: "normal", faces: {}, notes: "" };
  const isAusente = toothData.status === "ausente";
  const faces = toothData.faces || {};

  const getFaceStyle = (faceName) => {
    const status = faces[faceName] || "nenhum";
    return FACE_COLORS[status] || FACE_COLORS.nenhum;
  };

  const hasNotes = !!toothData.notes;
  const hasPathology = Object.values(faces).some(v => v !== "nenhum");

  return (
    <div 
      onClick={onClick}
      className={`flex flex-col items-center p-1 rounded-lg border transition-all cursor-pointer select-none ${
        isActive 
          ? "border-[hsl(var(--brand))] bg-[hsl(var(--brand))]/5 ring-2 ring-[hsl(var(--brand))]/20 shadow-sm" 
          : hasPathology || hasNotes
            ? "border-gray-300 bg-amber-50/10 hover:bg-amber-50/30"
            : "border-transparent hover:bg-gray-50 hover:border-gray-200"
      }`}
      style={{ width: "42px" }}
      title={`Dente ${number}${hasNotes ? ` - Observação: ${toothData.notes}` : ""}`}
    >
      <span className={`text-[10px] font-bold mb-1 ${isActive ? "text-[hsl(var(--brand))]" : "text-gray-500"}`}>{number}</span>
      <div className="relative w-8 h-8 flex items-center justify-center">
        <svg viewBox="0 0 40 40" className="w-full h-full">
          {/* Vestibular (Top) */}
          <path 
            d="M 0,0 L 40,0 L 28,12 L 12,12 Z" 
            fill={getFaceStyle("vestibular").fill} 
            stroke={getFaceStyle("vestibular").stroke} 
            strokeWidth="1.2"
          />
          {/* Distal (Right) */}
          <path 
            d="M 40,0 L 28,12 L 28,28 L 40,40 Z" 
            fill={getFaceStyle("distal").fill} 
            stroke={getFaceStyle("distal").stroke} 
            strokeWidth="1.2"
          />
          {/* Palatina/Lingual (Bottom) */}
          <path 
            d="M 12,28 L 28,28 L 40,40 L 0,40 Z" 
            fill={getFaceStyle("palatina").fill} 
            stroke={getFaceStyle("palatina").stroke} 
            strokeWidth="1.2"
          />
          {/* Mesial (Left) */}
          <path 
            d="M 0,0 L 12,12 L 12,28 L 0,40 Z" 
            fill={getFaceStyle("mesial").fill} 
            stroke={getFaceStyle("mesial").stroke} 
            strokeWidth="1.2"
          />
          {/* Oclusal/Incisal (Center) */}
          <path 
            d="M 12,12 L 28,12 L 28,28 L 12,28 Z" 
            fill={getFaceStyle("oclusal").fill} 
            stroke={getFaceStyle("oclusal").stroke} 
            strokeWidth="1.2"
          />
          {/* Ausente X Overlay */}
          {isAusente && (
            <>
              <line x1="4" y1="4" x2="36" y2="36" stroke="rgba(107, 114, 128, 0.75)" strokeWidth="3.5" strokeLinecap="round" />
              <line x1="36" y1="4" x2="4" y2="36" stroke="rgba(107, 114, 128, 0.75)" strokeWidth="3.5" strokeLinecap="round" />
            </>
          )}
        </svg>

        {/* Ponto indicador de notas */}
        {hasNotes && (
          <span className="absolute bottom-[-2px] right-[-2px] w-2 h-2 rounded-full bg-yellow-500 border border-white shadow-sm" />
        )}
      </div>
    </div>
  );
}
